import {
	Experimental_CssVarsProvider as CssVarsProvider,
	experimental_extendTheme as extendTheme,
} from "@mui/material/styles";
import ThemeSet from "./themeSet.json";
import * as ImagesMewahgps from "@assets/images/mewahgps";
import * as ImagesGreenhaul from "@assets/images/greenhaul";

const key = (process.env.REACT_APP_GPS_COMPANY || "").toLowerCase();

function withBase(base, obj) {
	return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, `${base}${v}`]));
}

const themeSwapper = () => {
	const selected = ThemeSet[key];
	if (!selected) {
		throw new Error(
			`Unknown company theme "${key}". Options: ${Object.keys(ThemeSet).join(", ")}`
		);
	}
	return selected;
};

const themeBuilder = (cfg) => {
	if (typeof window !== "undefined") {
		document.documentElement.style.setProperty("--custom-primary", cfg.primary);
		document.documentElement.style.setProperty("--custom-primary-dark", cfg.primary + "E6");
		document.documentElement.style.setProperty("--custom-primary-hover", cfg.primary + "14");
		document.documentElement.style.setProperty("--custom-primary-active", cfg.primary + "1F");
		document.documentElement.style.setProperty("--custom-primary-shadow", cfg.primary + "33");

		const favicon = document.querySelector("link[rel='icon']");
		const currentImageSet = imageSwapper();
		if (favicon && currentImageSet.AppIcon) {
			favicon.href = currentImageSet.AppIcon;
		}
	}
	return extendTheme({
		colorSchemes: {
			light: {
				palette: {
					primary: { main: cfg.primary },
					secondary: { main: cfg.secondary },
					background: { default: cfg.backgroundDefault, paper: cfg.backgroundPaper },
					text: { primary: cfg.textPrimary, secondary: cfg.textSecondary },
				},
			},
		},
		typography: {
			fontFamily: '"Outfit", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
			h1: { fontWeight: 700 },
			h2: { fontWeight: 700 },
			h3: { fontWeight: 600 },
			button: { textTransform: 'none', fontWeight: 600 },
		},
		boxShadows: {
			soft: "0 4px 20px 0 rgba(0,0,0,0.05)",
			card: "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)",
			glass: "0 8px 32px 0 rgba(31, 38, 135, 0.37)",
		},
		shape: { borderRadius: cfg.borderRadius ?? 12 },
		components: {
			MuiPaper: {
				styleOverrides: {
					root: {
						backgroundImage: 'none',
						boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
					},
				},
			},
			MuiButton: {
				styleOverrides: {
					root: {
						borderRadius: '12px',
						padding: '10px 20px',
						transition: 'all 0.2s ease-in-out',
						'&:hover': {
							transform: 'translateY(-2px)',
							boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
						},
					},
					contained: ({ theme }) => ({
						height: "45px",
						backgroundColor: theme.vars.palette.primary.main,
						"&:hover": { backgroundColor: theme.vars.palette.primary.dark },
					}),
				},
			},
			MuiCard: {
				styleOverrides: {
					root: {
						borderRadius: '20px',
						backdropFilter: 'blur(10px)',
						border: '1px solid rgba(255, 255, 255, 0.3)',
					},
				},
			},
			MuiInputBase: {
				styleOverrides: {
					root: ({ theme }) => ({
						background: "white !important",
						height: "45px",
						borderRadius: '12px !important',
						boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05) !important",
						border: '1px solid #E2E8F0 !important',
						padding: "0 10px !important",
						'&.Mui-focused': {
							borderColor: theme.vars.palette.primary.main + ' !important',
							boxShadow: `0 0 0 3px ${theme.vars.palette.primary.main}20 !important`,
						},
					}),
				},
			},
			MuiCheckbox: {
				styleOverrides: {
					root: {
						height: "15px !important",
						boxShadow: "none !important",
					},
				},
			},
			MuiInputLabel: {
				styleOverrides: {
					outlined: {
						transform: "translate(14px, 12px) scale(1)",
						"&.MuiInputLabel-shrink": {
							transform: "translate(14px, -6px) scale(0.75)",
						},
					},
				},
			},
		},
	});
};

const IMAGE_MODULES = {
	mewahgps: ImagesMewahgps,
	greenhaul: ImagesGreenhaul,
};

const imageSwapper = () => {
	// const imageBase = "@assets/images/";
	const selected = IMAGE_MODULES[key];
	if (!selected) {
		throw new Error(
			`Unknown company images "${key}". Options: ${Object.keys(IMAGE_MODULES).join(", ")}`
		);
	}
	return selected;
};

const raw = ThemeSet[key] || ThemeSet.default;
const MuiTheme = themeBuilder(raw);
const Theme = themeSwapper();
const Image = imageSwapper();

export { Theme, Image, MuiTheme };
